import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  const sessionId = searchParams.get('session_id');
  const timeoutRef = useRef(null);
  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (!sessionId || !token) return;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/payments/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPaymentInfo(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      timeoutRef.current = setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
    }
  }, [sessionId, token]);
  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [sessionId, token, pollPaymentStatus]);




  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="max-w-md w-full" data-testid="payment-result-card">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-[#D4FF00] animate-spin mb-6" />
              <h1 className="font-heading font-bold text-2xl mb-2">Vérification du paiement...</h1>
              <p className="text-muted-foreground">Veuillez patienter quelques instants</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="font-heading font-bold text-2xl mb-2">Paiement réussi !</h1>
              <p className="text-muted-foreground mb-6">
                Votre réservation a été confirmée. Vous recevrez un email de confirmation.
              </p>
              {paymentInfo && (
                <p className="text-lg font-bold mb-6">
                  Montant : {(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency.toUpperCase()}
                </p>
              )}
              <Button
                onClick={() => navigate('/bookings')}
                className="bg-[#D4FF00] text-black hover:bg-[#bfe600] font-bold uppercase tracking-wide"
                data-testid="view-bookings-btn"
              >
                Voir mes réservations
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                <XCircle className="w-12 h-12 text-yellow-600" />
              </div>
              <h1 className="font-heading font-bold text-2xl mb-2">Session expirée</h1>
              <p className="text-muted-foreground mb-6">
                La session de paiement a expiré. Veuillez réessayer.
              </p>
              <Button
                onClick={() => navigate('/bookings')}
                className="bg-primary text-primary-foreground font-bold"
                data-testid="retry-payment-btn"
              >
                Retourner aux réservations
              </Button>
            </>
          )}

          {(status === 'error' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
              <h1 className="font-heading font-bold text-2xl mb-2">Erreur</h1>
              <p className="text-muted-foreground mb-6">
                Une erreur est survenue lors de la vérification du paiement.
                Veuillez vérifier vos réservations ou réessayer.
              </p>
              <Button
                onClick={() => navigate('/bookings')}
                className="bg-primary text-primary-foreground font-bold"
                data-testid="check-bookings-btn"
              >
                Vérifier mes réservations
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
